import type { HttpService } from '../App/services/http-service';
import type { MxApplicationService } from '../App/services/mx-application-service';
import type { UserProfileService } from '../App/services/user-profile-service';
import type { ExportConfig, Orientation } from './export-dialog/export-dialog.component';
import type { ThemeService } from '../Theme/mx-theme.service';

interface ImageBlockData {
	image: HTMLImageElement;
	width: number;
	height: number;
}

type ExportFormat = 'png' | 'jpeg';

/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.PrintingMarkupService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides methods to getting header and footer markup for printed page layout.
 */
export class PrintingService {
	public readonly a4Width = (595.28 * 72) / 96;
	public readonly a4Height = (841.89 * 72) / 96;
	public readonly imageWidth = 2000;

	static readonly $inject = ['mx.SolutionBuilderAgent.Http', 'mx.shell.AppsService', 'mx.shell.UserProfileService', 'mx.app.ThemeService'];

	constructor(
		private readonly http: HttpService,
		private readonly applicationService: MxApplicationService,
		private readonly userProfileService: UserProfileService,
		private readonly themeService: ThemeService
	) {}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PrintingMarkupService#getCurrentApplicationPrintingMarkup
	 *
	 * @description
	 * send requests to api to get print layout info for current application
	 *
	 * @returns an object which contains print layout info for current application
	 */
	public async getCurrentApplicationPrintingMarkup(title = '', logo = '') {
		const app = this.applicationService.getCurrentApplication();
		const requestParams = app.value ? { applicationId: app.value } : {};
		const markupData = await this.http.get<{ Header?: string; Footer?: string } | null>('/api/application/printlayout', requestParams);
		const date = new Date().toLocaleDateString();

		const replacer = (data?: string) =>
			data
				?.replaceAll('@USER@', this.userProfileService.userInfo?.accountName ?? '')
				.replaceAll('@CURRENT_DATE@', date)
				.replaceAll('@TITLE@', title)
				.replaceAll('@PAGE@', `<div class="mx-export-dialog--page"></div>`)
				.replaceAll(/@LOGOx(?<width>\d+)x(?<height>\d+)@/g, (matches, width = 66, height = 13) => {
					return logo ? `<img src=${logo} width="${width}" height="${height}" alt="logo" />` : '';
				});

		return !markupData
			? null
			: {
					printingHeader: replacer(markupData.Header) ?? '',
					printingFooter: replacer(markupData.Footer) ?? '',
				};
	}

	async getImageAsBase64(contentEl: HTMLElement, headerEl?: HTMLElement, footerEl?: HTMLElement, format: ExportFormat = 'png') {
		return (await this.getPages(contentEl, this.imageWidth, undefined, headerEl, footerEl, 1, format))[0];
	}

	async getPdfAsBase64(contentEl: HTMLElement, orientation: Orientation = 'landscape', headerEl?: HTMLElement, footerEl?: HTMLElement) {
		const pdf = await this.getPDF(contentEl, orientation, headerEl, footerEl);
		return pdf.output('datauristring');
	}

	async loadImage(contentEl: HTMLElement, format: ExportFormat = 'png', name = 'my-image-name', width = this.imageWidth, headerEl?: HTMLElement, footerEl?: HTMLElement) {
		const pages = await this.getPages(contentEl, width, undefined, headerEl, footerEl, 1, format);

		const link = document.createElement('a');
		link.download = `${name}.${format}`;
		link.href = pages[0];
		link.click();
	}

	public async printNode(config: ExportConfig) {
		await new Promise<void>((resolve) => {
			this.handlerBeforePrint(config).then(() => {
				print();
			});

			window.addEventListener(
				'afterprint',
				() => {
					this.handlerAfterPrint();
					resolve();
				},
				{
					once: true,
					capture: true,
				}
			);
		});
	}

	private async handlerBeforePrint({ orientation, contentNode, headerNode, footerNode, themeId }: ExportConfig) {
		if ($('#printing-container').length) {
			return;
		}
		$(document.body).children().addClass('printing-hide');
		$('body').prepend(this.getPrintingStyles(orientation, headerNode, footerNode));

		const printingContainer = $('<div id="printing-container">').html((headerNode?.outerHTML ?? '') + contentNode.outerHTML + (footerNode?.outerHTML ?? ''));
		const theme = themeId ? await this.themeService.getTheme(themeId) : await this.themeService.getCurrentTheme();
		this.themeService.applyCssVariables('printing-container', theme, document, printingContainer[0]);

		$(document.body).prepend(printingContainer);
	}

	private handlerAfterPrint() {
		$(document.body).children().removeClass('printing-hide');
		$('#printing-container').remove();
		$('#mx-print-styles').remove();
	}

	private getPrintingStyles(orientation: Orientation, headerEl?: HTMLElement, footerEl?: HTMLElement) {
		return `
			<style type="text/css" id="mx-print-styles">
				@media print {
					* {
						-webkit-print-color-adjust: exact !important;
						color-adjust: exact !important;
						print-color-adjust: exact !important;
					 }

					@page {
						size: 2000px ${orientation === 'landscape' ? 1414 : 2828}px;
						margin: 5mm;
					}

					.printing-hide {
						display: none;
					}

					.mx-export-dialog--preview-header, .mx-export-dialog--preview-footer {
						max-width: 100%;
						width: 100%;
						position: fixed;
						z-index: 1000;
						height: 30px;
					}

					.mx-export-dialog--preview-header {
						top: 0;
						height: ${headerEl?.offsetHeight ?? 0}px;
					}

					.mx-export-dialog--preview-footer {
						bottom: 0;
						height: ${footerEl?.offsetHeight ?? 0}px;
					}

					.mx-export-dialog--preview {
						padding-top: ${headerEl?.offsetHeight ?? 0}px;
						padding-bottom: ${footerEl?.offsetHeight ?? 0}px;
						transform-origin: top left;
					}

					.mx-export-dialog--page {
						counter-increment: pageNumber 1;
    					content: counter(pageNumber);
					}
				}
			</style>`;
	}

	private async getPDF(contentEl: HTMLElement, orientation: Orientation = 'landscape', headerEl?: HTMLElement, footerEl?: HTMLElement) {
		const { jsPDF } = await import('jspdf');

		const docWidth = orientation === 'landscape' ? this.a4Height : this.a4Width;
		const docHeight = orientation === 'landscape' ? this.a4Width : this.a4Height;

		const pdf = new jsPDF(orientation, 'px', 'a4');

		const pages = await this.getPages(contentEl, docWidth, docHeight, headerEl, footerEl, 5, 'jpeg');

		pages.forEach((page, index) => {
			pdf.addImage({
				imageData: page,
				format: 'JPEG',
				compression: 'MEDIUM',
				x: 0,
				y: 0,
				width: docWidth,
				height: docHeight,
			});

			if (index !== pages.length - 1) {
				pdf.addPage('a4', orientation);
			}
		});

		return pdf;
	}

	async loadPDF(contentEl: HTMLElement, name = 'my-doc', orientation: Orientation = 'landscape', headerEl?: HTMLElement, footerEl?: HTMLElement) {
		const pdf = await this.getPDF(contentEl, orientation, headerEl, footerEl);
		pdf.save(`${name}.pdf`);
	}

	private getPageCanvas(content: ImageBlockData, pageHeight: number, page: number, header?: ImageBlockData, footer?: ImageBlockData) {
		const pageCanvas = document.createElement('canvas');
		document.body.append(pageCanvas);
		const fullHeight = pageHeight + (header?.height ?? 0) + (footer?.height ?? 0);
		pageCanvas.setAttribute('width', content.width.toString());
		pageCanvas.setAttribute('height', fullHeight.toString());

		const ctx = pageCanvas.getContext('2d');
		if (!ctx) {
			throw new Error('canvas context cannot be created');
		}

		ctx.fillStyle = 'white';
		ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

		if (header) {
			ctx.drawImage(header.image, 0, 0, header.width, header.height, 0, 0, header.width, header.height);
		}

		ctx.drawImage(content.image, 0, (page - 1) * pageHeight, content.width, pageHeight, 0, header?.height ?? 0, content.width, pageHeight);

		if (footer) {
			ctx.drawImage(footer.image, 0, 0, footer.width, footer.height, 0, (header?.height ?? 0) + pageHeight, footer.width, footer.height);
		}

		return pageCanvas;
	}

	private handlePrintPaging(page?: number, el?: HTMLElement) {
		const pagingEl = el?.querySelector('.mx-export-dialog--page');

		if (pagingEl) {
			pagingEl.innerHTML = page?.toString() || '';
		}
	}

	private async getPage(
		format: ExportFormat,
		content: ImageBlockData,
		pageHeight: number,
		page?: number,
		headerEl?: HTMLElement,
		footerEl?: HTMLElement,
		pageWidth?: number,
		ppx?: number,
		quality = 1.0
	): Promise<string> {
		this.handlePrintPaging(page, headerEl);
		this.handlePrintPaging(page, footerEl);

		const header = headerEl && (await this.getImage(headerEl, pageWidth, ppx));
		const footer = footerEl && (await this.getImage(footerEl, pageWidth, ppx));

		const pageCanvas = this.getPageCanvas(content, pageHeight, page ?? 1, header, footer);

		header?.image.remove();
		footer?.image.remove();

		const pageImageData = pageCanvas.toDataURL(`image/${format}`, quality);
		pageCanvas.remove();
		return pageImageData;
	}

	private async getImage(node: HTMLElement, docWidth?: number, ppx?: number): Promise<ImageBlockData> {
		const imageWidth = typeof docWidth === 'number' && typeof ppx === 'number' ? docWidth * ppx : undefined;

		const { data, width, height } = await this.html2image(node, 'png', imageWidth);

		const image = new Image();
		image.src = data;
		const loadPromise = new Promise((resolve) => image.addEventListener('load', resolve));
		document.body.append(image);
		await loadPromise;

		return {
			image,
			width,
			height,
		};
	}

	private async getPages(contentEl: HTMLElement, pageWidth: number, pageHeight?: number, headerEl?: HTMLElement, footerEl?: HTMLElement, ppx = 1, format: ExportFormat = 'png') {
		const content = await this.getImage(contentEl, pageWidth, ppx);

		const headerHeight = headerEl ? (pageWidth * headerEl?.offsetHeight) / headerEl?.offsetWidth : 0;
		const footerHeight = footerEl ? (pageWidth * footerEl?.offsetHeight) / footerEl?.offsetWidth : 0;

		const pageContentHeight = pageHeight && pageHeight - headerHeight - footerHeight;

		if (typeof pageContentHeight === 'number' && pageContentHeight <= 0) {
			content.image.remove();
			console.error('no space in the document for the content (header/footer is too big?)');
			return [];
		}

		let page = 0;
		const pages: string[] = [];

		if (pageContentHeight) {
			const scale = content.width / pageWidth;
			const heightStepOriginal = pageContentHeight * scale;

			let heightLeftOriginal = content.height;

			while (heightLeftOriginal > 0) {
				page++;
				pages.push(await this.getPage(format, content, heightStepOriginal, page, headerEl, footerEl, pageWidth, ppx));
				heightLeftOriginal -= heightStepOriginal;
			}
		} else {
			pages.push(await this.getPage(format, content, content.height, undefined, headerEl, footerEl, pageWidth, ppx));
		}

		content.image.remove();

		return pages;
	}

	private async html2image(node: HTMLElement, format: ExportFormat, width?: number) {
		const { default: domtoimage } = await import('dom-to-image-more');

		const options: DomToImage.Options = {
			width: node.offsetWidth,
			height: node.offsetHeight,
		};

		if (typeof width === 'number') {
			const scale = width / node.offsetWidth;
			const height = node.offsetHeight * scale;

			options.width = width;
			options.height = height;
			options.style = {
				transform: `scale(${scale})`,
				transformOrigin: 'top left',
			};
		}

		return {
			data: await domtoimage[format === 'png' ? 'toPng' : 'toJpeg'](node, options),
			width: options.width as number,
			height: options.height as number,
		};
	}
}
